import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/commonAPI';

interface Sale {
    id: number;
    saleDate: string;
    tableId: number;
    totalAmount: number;
}

const SalesManagementPage = ({ storeId }: { storeId: number }) => {
    const navigate = useNavigate();
    const [date, setDate] = useState<string>(new Date().toISOString().slice(0, 10));
    const [salesData, setSalesData] = useState<Sale[]>([]);
    const [loading, setLoading] = useState(false);
    const [password, setPassword] = useState('');
    const [unlocked, setUnlocked] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const verifiedPassword = useRef('');
    const requestId = useRef(0);

    useEffect(() => () => {
        verifiedPassword.current = '';
        requestId.current += 1;
    }, []);

    const fetchSalesData = useCallback(async (candidate: string, queryDate: string) => {
        if (!candidate || !queryDate) return;
        const currentRequest = ++requestId.current;
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.post<Sale[]>(`/api/stores/${storeId}/admin/sales/query`, {
                date: queryDate, password: candidate,
            });
            if (currentRequest !== requestId.current) return;
            verifiedPassword.current = candidate;
            setUnlocked(true);
            setPassword('');
            setSalesData(res.data);
        } catch (error) {
            if (currentRequest !== requestId.current) return;
            setSalesData([]);
            if (axios.isAxiosError(error) && error.response?.status === 403) {
                verifiedPassword.current = '';
                setUnlocked(false);
                setPassword('');
                setError('비밀번호가 올바르지 않거나 해당 매장의 조회 권한이 없습니다.');
            } else {
                setError('매출 정보를 불러오지 못했습니다. 다시 시도해 주세요.');
            }
        } finally {
            if (currentRequest === requestId.current) setLoading(false);
        }
    }, [storeId]);

    const totalAmount = useMemo(
        () => salesData.reduce((sum, item) => sum + item.totalAmount, 0),
        [salesData]
    );

    return (
        <div style={containerStyle}>
            <div style={headerStyle}>
                <button onClick={() => navigate('/')} style={backButtonStyle} type="button">
                    ←
                </button>
                <h2 style={{ margin: 0, flexGrow: 1 }}>매출 관리</h2>
            </div>

            {error && <p role="alert" style={{ color: '#b91c1c' }}>{error}</p>}
            {!unlocked ? (
                <form onSubmit={event => { event.preventDefault(); if (!loading) void fetchSalesData(password, date); }}
                    style={{ maxWidth: 400, margin: '40px auto', padding: 24, border: '1px solid #e2e8f0', borderRadius: 8 }}>
                    <h3>비밀번호 확인</h3>
                    <p>계정의 비밀번호를 다시 입력해 주세요.</p>
                    <label htmlFor="sales-password" style={labelStyle}>비밀번호</label>
                    <input id="sales-password" type="password" autoComplete="current-password" autoFocus required
                        value={password} disabled={loading} onChange={event => setPassword(event.target.value)}
                        style={{ ...dateInputStyle, display: 'block', width: '100%', boxSizing: 'border-box', margin: '8px 0 16px' }} />
                    <button type="submit" disabled={loading || !password} style={searchButtonStyle}>
                        {loading ? '확인 중...' : '확인'}
                    </button>
                </form>
            ) : <>
            <div style={filterRowStyle}>
                <label htmlFor="sales-date" style={labelStyle}>조회 날짜</label>
                <input
                    id="sales-date"
                    type="date"
                    value={date}
                    onChange={event => {
                        setDate(event.target.value);
                        setSalesData([]);
                        void fetchSalesData(verifiedPassword.current, event.target.value);
                    }}
                    style={dateInputStyle}
                />
                <button onClick={() => fetchSalesData(verifiedPassword.current, date)} disabled={loading || !date} style={searchButtonStyle} type="button">
                    조회
                </button>
            </div>

            <div style={totalPanelStyle}>
                선택한 날짜의 총 매출: <strong>{totalAmount.toLocaleString()}원</strong>
            </div>

            {loading ? (
                <p>불러오는 중...</p>
            ) : (
                <table style={tableStyle}>
                    <thead>
                    <tr style={tableHeaderRowStyle}>
                        <th style={cellStyle}>판매일</th>
                        <th style={cellStyle}>테이블 ID</th>
                        <th style={cellStyle}>매출액</th>
                    </tr>
                    </thead>
                    <tbody>
                    {salesData.length > 0 ? (
                        salesData.map((sale) => (
                            <tr key={sale.id} style={tableRowStyle}>
                                <td style={cellStyle}>{sale.saleDate}</td>
                                <td style={cellStyle}>{sale.tableId}</td>
                                <td style={cellStyle}>{sale.totalAmount.toLocaleString()}원</td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={3} style={emptyCellStyle}>
                                해당 날짜의 매출 내역이 없습니다.
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            )}
            </>}
        </div>
    );
};

const containerStyle: React.CSSProperties = {
    padding: 20,
    fontFamily: 'sans-serif',
};

const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 15,
    marginBottom: 20,
    paddingBottom: 20,
    borderBottom: '1px solid #eee',
};

const backButtonStyle: React.CSSProperties = {
    padding: '10px 15px',
    fontSize: 18,
    lineHeight: 1,
    backgroundColor: '#6c757d',
    color: '#fff',
    border: 'none',
    borderRadius: 5,
    cursor: 'pointer',
};

const filterRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
};

const labelStyle: React.CSSProperties = {
    fontWeight: 700,
};

const dateInputStyle: React.CSSProperties = {
    minHeight: 38,
    padding: '0 10px',
    border: '1px solid #ccc',
    borderRadius: 6,
};

const searchButtonStyle: React.CSSProperties = {
    minHeight: 38,
    padding: '0 16px',
    border: 'none',
    borderRadius: 6,
    backgroundColor: '#2563eb',
    color: '#fff',
    fontWeight: 700,
    cursor: 'pointer',
};

const totalPanelStyle: React.CSSProperties = {
    marginTop: 20,
    padding: 14,
    borderRadius: 8,
    backgroundColor: '#f8fafc',
    fontWeight: 700,
};

const tableStyle: React.CSSProperties = {
    marginTop: 20,
    width: '100%',
    borderCollapse: 'collapse',
};

const tableHeaderRowStyle: React.CSSProperties = {
    borderBottom: '1px solid #aaa',
};

const tableRowStyle: React.CSSProperties = {
    borderBottom: '1px solid #ddd',
};

const cellStyle: React.CSSProperties = {
    padding: 12,
    textAlign: 'left',
};

const emptyCellStyle: React.CSSProperties = {
    padding: 16,
    textAlign: 'center',
    color: '#666',
};

export default SalesManagementPage;
